import { useEffect } from "react";

import { UseQueryOptions, useSuspenseQuery } from "@tanstack/react-query";

import { useGetPublicationListFilterData } from "@/components/business/PublicationListContainer/hooks/useGetPublicationListFilterData";
import { queryClient } from "@api/queryClient";
import { QUERY_KEYS } from "@api/queryKeys";
import { usePublicationListFilterState } from "@stores/publicationList";

import { adaptPublicationListData } from "./adaptPublicationList.data";
import { publicationListApis } from "./publicationList.service";

import type {
  PostPublicationListFilterResponse,
  PublicationListFilteringData,
} from "@interface/publicationList.interface";

export const usePublicationListFilterQuery = (
  options?: UseQueryOptions<
    PostPublicationListFilterResponse,
    unknown,
    PostPublicationListFilterResponse,
    [string, PublicationListFilteringData]
  >,
) => {
  const filteringData = useGetPublicationListFilterData();

  const [, setPublicationListFilter] = usePublicationListFilterState();
  const query = useSuspenseQuery({
    queryKey: [QUERY_KEYS.publicationList, filteringData],
    queryFn: async () => {
      const response = await publicationListApis.post(filteringData);
      return response;
    },
    select: (data) => adaptPublicationListData(data),
    staleTime: 0,
    refetchInterval: 2000,
    ...options,
  });

  useEffect(() => {
    setPublicationListFilter((prev) => ({
      ...prev,
      maxPage: query.data.totalPages,
      publicationListDataTotal: query.data.total,
    }));
  }, [query.data.total, query.data.totalPages, setPublicationListFilter]);

  return query;
};

export const invalidatePublicationListFilterQuery = () => {
  return queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.publicationList],
  });
};
